import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '../../../base';
import {
    IJobApplication,
    IServerJobApplicationTrailParams,
    IServerJobApplicationTrailRelatedObject,
    JobApplicationTrailActionTypes,
} from '../../../common';
import NoInterviewTrail from './NoInterviewTrail';
import TrailItem from './TrailItem';

interface Props {
    jobApplication: IJobApplication;
}

const InterviewTrail = (props: Props) => {
    const { jobApplication } = props;
    const { steps, trail } = jobApplication;
    const { t } = useTranslation();

    const getStepNameById = (stepId: number) => {
        return steps.find((step) => step.id == stepId)?.name;
    };

    const getTrailActionText = (
        actionType: JobApplicationTrailActionTypes,
        params: IServerJobApplicationTrailParams,
        relatedObject: IServerJobApplicationTrailRelatedObject
    ): string => {
        const candidateFullName = `${jobApplication.firstName} ${jobApplication.lastName}`;

        switch (actionType) {
            case JobApplicationTrailActionTypes.comment:
                const stepId = relatedObject.step || 0;
                return t('trail.actions.comment', {
                    candidateName: candidateFullName,
                    stepName: getStepNameById(stepId),
                });

            case JobApplicationTrailActionTypes.stageChange:
                return t('trail.actions.stageChange', {
                    originalStep: params.original_step,
                    newStep: params.new_step,
                    candidateName: candidateFullName,
                });

            case JobApplicationTrailActionTypes.statusChange:
                return t('trail.actions.statusChange', {
                    fromStatus: t(`statuses.${params.from_status}`),
                    toStatus: t(`statuses.${params.to_status}`),
                    candidateName: candidateFullName,
                });

            case JobApplicationTrailActionTypes.rejected: {
                const reason = params.reject_reason || '';
                const comment = params.reject_comment || '';
                let displayReason = '';

                if (reason && comment) {
                    displayReason = `${reason} (${t('trail.note')}: ${comment})`;
                } else {
                    displayReason = reason || comment;
                }

                return t('trail.actions.rejected', { reason: displayReason });
            }

            case JobApplicationTrailActionTypes.withdrew:
                return t('trail.actions.withdrew');

            case JobApplicationTrailActionTypes.noteAdded:
                return t('trail.actions.noteAdded');

            case JobApplicationTrailActionTypes.hired:
                return t('trail.actions.hired', {
                    candidateName: candidateFullName,
                });

            case JobApplicationTrailActionTypes.interviewScheduled:
                return t('trail.actions.interviewScheduled');

            case JobApplicationTrailActionTypes.interviewUpdated:
                return t('trail.actions.interviewUpdated');

            case JobApplicationTrailActionTypes.interviewDeleted:
                return t('trail.actions.interviewDeleted');
            default:
                return '';
        }
    };

    const sortedTrail = trail
        ? [...trail].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        : [];

    return (
        <Box>
            {sortedTrail.length > 0 ? (
                <Box>
                    {sortedTrail.map((trailItem) => (
                        <TrailItem
                            key={trailItem.id}
                            trailItem={trailItem}
                            getTrailActionText={getTrailActionText}
                        />
                    ))}
                </Box>
            ) : (
                <NoInterviewTrail />
            )}
        </Box>
    );
};

export default InterviewTrail;
